// Keyboard navigation for the filmstrip, as a pure function so it can be
// tested without mounting the workspace.
//
// The filmstrip is a WAI-ARIA tablist, and its tabs carry a roving tabindex:
// only the selected tab is in the document tab order. That half alone is worse
// than nothing — it takes every other thumbnail *out* of the tab order while
// leaving no way to reach them — so the arrow-key half is not optional, and
// keeping it here means a test can prove it exists.

/** Where each claimed key goes, given a clamped index and the last one.
 *  Both ends stop rather than wrap: a deck is ordered, and wrapping from the
 *  closing slide back to the cover reads as a mis-key rather than a move. */
const MOVES = {
  ArrowRight: (from: number, last: number) => Math.min(last, from + 1),
  ArrowLeft: (from: number) => Math.max(0, from - 1),
  Home: () => 0,
  End: (_from: number, last: number) => last,
} satisfies Record<string, (from: number, last: number) => number>;

/** The keys the filmstrip claims — derived from the table above rather than
 *  restated, so a key cannot be handled without appearing here. The component's
 *  preventDefault is gated on a non-null target, and this is what lets a test
 *  sweep every claimed key and prove that gate never swallows one it ignores. */
export const FILMSTRIP_KEYS = Object.keys(MOVES) as (keyof typeof MOVES)[];

/** The slide a key moves to, or null when the key is not ours to handle. */
export function filmstripTarget(key: string, at: number, count: number): number | null {
  // hasOwn, not a bare lookup: every object literal inherits Object.prototype,
  // so `MOVES.constructor` and `MOVES.valueOf` are functions and a plain
  // lookup would call one. No KeyboardEvent.key is ever named for them, but
  // the contract here is "null for anything not ours" and that should hold for
  // any string, not only the ones a keyboard happens to produce.
  if (!Object.hasOwn(MOVES, key) || count < 1) return null;
  const move = MOVES[key as keyof typeof MOVES];
  const last = count - 1;
  return move(Math.min(Math.max(at, 0), last), last);
}
